import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import logo from "../logo.png";

const API_BASE = process.env.REACT_APP_API_URL || "";

export default function Sidebar({
  chats,
  activeChat,
  onNewChat,
  onSelectChat,
  onDeleteChat,
  onRenameChat,
  onBulkDelete,
  isOpen,
  onToggle,
  isAdmin,
  onOpenAdmin,
  saveHistory,
  onToggleSaveHistory,
  activeTenant,
  personalMode,
  onOpenTenantSwitcher,
  onOpenTenantSettings,
  onOpenWorkflows,
  onOpenTasks,
  onOpenKnowledge,
}) {
  const { user, token, logout } = useAuth();
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showSettings, setShowSettings] = useState(false);
  const [showTenantMenu, setShowTenantMenu] = useState(false);
  const isTenantAdmin = activeTenant && (activeTenant.role === 'admin' || activeTenant.role === 'owner');
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");
    if (newPassword !== confirmNewPassword) {
      setPasswordError("Passwords do not match");
      return;
    }
    setPasswordLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/password`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setPasswordSuccess("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
      setTimeout(() => {
        setShowPasswordChange(false);
        setPasswordSuccess("");
      }, 1500);
    } catch (err) {
      setPasswordError(err.message);
    } finally {
      setPasswordLoading(false);
    }
  };

  const startRename = (chat) => {
    setEditingId(chat._id);
    setEditTitle(chat.title);
  };

  const submitRename = (chatId) => {
    if (editTitle.trim()) onRenameChat(chatId, editTitle.trim());
    setEditingId(null);
  };

  const toggleSelect = (chatId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(chatId)) next.delete(chatId);
      else next.add(chatId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === chats.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(chats.map((c) => c._id)));
    }
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    const isAll = selectedIds.size === chats.length;
    if (isAll) {
      onBulkDelete({ all: true });
    } else {
      onBulkDelete({ chatIds: Array.from(selectedIds) });
    }
    setSelectedIds(new Set());
    setSelectMode(false);
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  return (
    <div className={`sidebar ${isOpen ? "open" : "closed"}`}>
      <div className="sidebar-header">
        <div className="sidebar-brand">
          <span className="logo-icon">
            <img
              src={logo}
              alt="nextversion AI Logo"
              style={{
                width: "240px",
                height: "65px",
                objectFit: "contain",
              }}
            />
          </span>
        </div>
        <button className="icon-btn" onClick={onToggle} title="Toggle sidebar">
          ✕
        </button>
      </div>

      {activeTenant && (
        <div className="tenant-switcher">
          <button
            className="tenant-switcher-btn"
            onClick={() => setShowTenantMenu(v => !v)}
            title="Mandant wechseln"
          >
            <span className="tenant-icon">🏢</span>
            <span className="tenant-name">{activeTenant.name}</span>
            <span className="tenant-role-tag">{activeTenant.role}</span>
            <span className="tenant-chevron">{showTenantMenu ? '▲' : '▼'}</span>
          </button>
          {showTenantMenu && (
            <div className="tenant-menu">
              {isTenantAdmin && (
                <button
                  className="tenant-menu-item"
                  onClick={() => { onOpenTenantSettings(); setShowTenantMenu(false); }}
                >
                  ⚙️ Mandanten-Einstellungen
                </button>
              )}
              <button
                className="tenant-menu-item"
                onClick={() => { onOpenTenantSwitcher(); setShowTenantMenu(false); }}
              >
                ↩ Mandanten wechseln
              </button>
            </div>
          )}
        </div>
      )}

      {/* Privat-Modus: kein Mandant aktiv, abgespeckte Sidebar. Klick auf den
          Header → zurück zur Mandanten-Auswahl. */}
      {personalMode && (
        <div className="tenant-switcher">
          <button
            className="tenant-switcher-btn personal"
            onClick={onOpenTenantSwitcher}
            title="Mandanten-Auswahl öffnen"
          >
            <span className="tenant-icon">🏠</span>
            <span className="tenant-name">Privat</span>
            <span className="tenant-role-tag">solo</span>
            <span className="tenant-chevron">↩</span>
          </button>
        </div>
      )}

      <button className="new-chat-btn" onClick={onNewChat}>
        <span>+</span> New Chat
      </button>

      {activeTenant && onOpenWorkflows && (
        <button className="workflows-nav-btn" onClick={onOpenWorkflows} title="No-Code Workflows">
          <span>⚡</span> Workflows
        </button>
      )}

      {activeTenant && onOpenTasks && (
        <button className="workflows-nav-btn" onClick={onOpenTasks} title="Aufgaben aus Workflows oder manuell">
          <span>📋</span> Aufgaben
        </button>
      )}

      {activeTenant && onOpenKnowledge && (
        <button className="workflows-nav-btn" onClick={onOpenKnowledge} title="Dokumente für RAG (Retrieval-Augmented Generation)">
          <span>📚</span> Wissen
        </button>
      )}

      {/* Select mode toolbar */}
      {chats.length > 0 && (
        <div className="select-toolbar">
          {!selectMode ? (
            <button
              className="select-mode-btn"
              onClick={() => setSelectMode(true)}
            >
              ☐ Select
            </button>
          ) : (
            <div className="select-actions">
              <label className="select-all-label">
                <input
                  type="checkbox"
                  checked={
                    selectedIds.size === chats.length && chats.length > 0
                  }
                  onChange={toggleSelectAll}
                />
                All ({selectedIds.size}/{chats.length})
              </label>
              <button
                className="bulk-delete-btn"
                onClick={handleBulkDelete}
                disabled={selectedIds.size === 0}
              >
                🗑️ Delete
              </button>
              <button className="cancel-select-btn" onClick={exitSelectMode}>
                ✕
              </button>
            </div>
          )}
        </div>
      )}

      <div className="chat-list">
        {chats.map((chat) => (
          <div
            key={chat._id}
            className={`chat-item ${activeChat?._id === chat._id ? "active" : ""} ${selectMode && selectedIds.has(chat._id) ? "selected" : ""}`}
            onClick={() =>
              selectMode ? toggleSelect(chat._id) : onSelectChat(chat._id)
            }
          >
            {selectMode && (
              <input
                type="checkbox"
                className="chat-checkbox"
                checked={selectedIds.has(chat._id)}
                onChange={() => toggleSelect(chat._id)}
                onClick={(e) => e.stopPropagation()}
              />
            )}
            {editingId === chat._id ? (
              <input
                className="rename-input"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onBlur={() => submitRename(chat._id)}
                onKeyDown={(e) => e.key === "Enter" && submitRename(chat._id)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            ) : (
              <>
                {!selectMode && <span className="chat-icon">💬</span>}
                <span className="chat-title">{chat.title}</span>
                {!selectMode && (
                  <div className="chat-actions">
                    <button
                      className="action-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        startRename(chat);
                      }}
                      title="Rename"
                    >
                      ✏️
                    </button>
                    <button
                      className="action-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteChat(chat._id);
                      }}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {/* Settings panel */}
      {showSettings && (
        <div className="settings-panel">
          <div className="settings-header">
            <span>Settings</span>
            <button className="icon-btn" onClick={() => setShowSettings(false)}>
              ✕
            </button>
          </div>
          <div className="settings-option">
            <label className="toggle-label">
              <span>Save Chat History</span>
              <div
                className={`toggle-switch ${saveHistory ? "on" : "off"}`}
                onClick={onToggleSaveHistory}
              >
                <div className="toggle-knob" />
              </div>
            </label>
            <p className="settings-hint">
              {saveHistory
                ? "Chats are saved between sessions."
                : "Chats will not be saved. History is cleared on reload."}
            </p>
          </div>
          <div className="settings-divider" />
          {!showPasswordChange ? (
            <button
              className="settings-link-btn"
              onClick={() => setShowPasswordChange(true)}
            >
              Change Password
            </button>
          ) : (
            <form
              className="password-change-form"
              onSubmit={handleChangePassword}
            >
              {passwordError && (
                <div className="error-message small">{passwordError}</div>
              )}
              {passwordSuccess && (
                <div className="success-message small">{passwordSuccess}</div>
              )}
              <input
                type="password"
                placeholder="Current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
              <input
                type="password"
                placeholder="New password (min. 6)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
              />
              <input
                type="password"
                placeholder="Confirm new password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
              />
              <div className="password-change-actions">
                <button
                  type="submit"
                  className="btn-sm-primary"
                  disabled={passwordLoading}
                >
                  {passwordLoading ? "..." : "Save"}
                </button>
                <button
                  type="button"
                  className="btn-sm-cancel"
                  onClick={() => {
                    setShowPasswordChange(false);
                    setPasswordError("");
                    setPasswordSuccess("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      <div className="sidebar-footer-copyright">
        © 2026 NextVersion AI. Created by Nima Habibi. All rights reserved.
      </div>

      <div className="sidebar-footer">
        <div className="user-info">
          <div className="user-avatar">
            {user?.username?.[0]?.toUpperCase()}
          </div>
          <span className="user-name">{user?.username}</span>
        </div>
        <div className="footer-actions">
          <button
            className="icon-btn"
            onClick={() => setShowSettings(!showSettings)}
            title="Konto & Sicherheit"
          >
            🔐
          </button>
          {isAdmin && (
            <button
              className="icon-btn"
              onClick={onOpenAdmin}
              title="Administration (Benutzer, Mandanten, Modelle)"
            >
              🛠️
            </button>
          )}
          <button
            className="icon-btn logout-btn"
            onClick={logout}
            title="Abmelden"
          >
            ⏻
          </button>
        </div>
      </div>
    </div>
  );
}
